import { createOutline, trashOutline } from 'ionicons/icons';
import { cloneDeep } from 'lodash';
import { useMemo } from 'react';
import { getLocalizedText } from '../../../../../../i18n/i18n.utils';
import { useFlowServices } from '../../../hook/flow-service.hooks';
import { FlowItem, FlowPanel } from '../../../model/flow.types';
import { createSectionsFromFlowItems } from '../../../util/flow-item.util';
import { useFlowRefContext } from '../../flow/flow-ref';
import { FlowItemBaseAction } from '../action/flow-item-base-action';
import { FlowItemCopyAction } from '../action/flow-item-copy-action';
import { FlowItemDeleteAction } from '../action/flow-item-delete-action';
import './flow-section-edit-action-bar.css';

export interface FlowSectionEditActionBarProps {
  flowItems: FlowItem[];
  sectionItem: FlowItem;
  readOnly?: boolean;
  onDelete: (items: FlowItem[]) => Promise<void>;
  onClosed?: () => void;
}

const FlowSectionEditActionBar = (props: FlowSectionEditActionBarProps) => {
  const { sectionItem, readOnly, onDelete, flowItems } = props;

  const allSectionItems = useMemo(() => {
    const sections = createSectionsFromFlowItems(flowItems);

    const section = sections.find(
      (s) => s?.section?.item?.id === sectionItem.id
    );

    const items = [
      sectionItem,
      ...(section?.items?.map((i) => i.item) ?? []),
    ] as FlowItem[];

    return cloneDeep(items);
  }, [sectionItem, flowItems]);

  const { flowAnalyticService } = useFlowServices();
  const { setSelectedItemIds } = useFlowRefContext();

  return (
    <div className="flow-section-edit-action-bar">
      <FlowItemDeleteAction
        label={getLocalizedText('common:remove_section')}
        icon={trashOutline}
        disabled={readOnly}
        onClick={() => {
          if (onDelete) {
            flowAnalyticService.trackSectionRemove();
            onDelete([sectionItem]);
          }
        }}
        color="var(--ion-text-color)"
      />
      <FlowItemBaseAction
        icon={createOutline}
        label={`${getLocalizedText('common:edit_all')} (${
          allSectionItems.length - 1 // We are excluding the "section" item from the count
        })`}
        onClick={() => {
          const sectionWithItemIds = allSectionItems.map((i) => i.id as string);
          const sectionItemIds = sectionWithItemIds.filter(
            (id) => id !== sectionItem.id
          );
          setSelectedItemIds(sectionItemIds, FlowPanel.MULTI_EDIT);
        }}
      />
      <FlowItemCopyAction
        label={`${getLocalizedText('common:copy_all')} (${
          allSectionItems.length
        })`}
        items={allSectionItems}
        onCopy={() => {
          flowAnalyticService.trackSectionCopyAll();
        }}
      />
      <FlowItemDeleteAction
        label={`${getLocalizedText('common:delete_all')} (${
          allSectionItems.length
        })`}
        disabled={readOnly}
        onClick={() => {
          if (onDelete) {
            flowAnalyticService.trackSectionRemoveAll();
            onDelete(allSectionItems);
          }
        }}
      />
    </div>
  );
};

export { FlowSectionEditActionBar };
